import type { SoundingLevel } from './mockWeatherData';

export type EmagramHourStatus =
  | 'not-triggered'
  | 'blocked'
  | 'weak'
  | 'usable-blue'
  | 'usable-cumulus'
  | 'overdeveloping';

export interface EmagramHourAnalysis {
  status: EmagramHourStatus;
  label: string;
  summary: string;
  surfaceTemp: number;
  surfaceDewpoint: number;
  lclAGL: number;
  lclPressure: number;
  thermalTopAGL: number;
  thermalTopPressure: number;
  usableCeilingAGL: number;
  cumulusBaseAGL: number;
  maxVz: number;
  maxBuoyancy: number;
  inversionBaseAGL: number | null;
  inversionTopAGL: number | null;
  cloudRisk: 'blue' | 'cumulus' | 'spreadout';
  windLayerKmh: number;
  windShearKmh: number;
  limitingFactors: string[];
}

const SOARING_ANALYSIS_TOP_AGL = 4500;

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

export function dewpointFromTempRh(tempC: number, rh: number): number {
  const safeRh = clamp(rh, 1, 100);
  const a = 17.625;
  const b = 243.04;
  const gamma = Math.log(safeRh / 100) + (a * tempC) / (b + tempC);
  return round1((b * gamma) / (a - gamma));
}

function pressureAtAltitude(levels: SoundingLevel[], agl: number, surfaceAlt: number): number {
  const targetAlt = surfaceAlt + agl;
  const sorted = [...levels].sort((a, b) => a.altitude - b.altitude);
  let previous = sorted[0];
  for (const level of sorted) {
    if (level.altitude >= targetAlt) {
      const span = Math.max(1, level.altitude - previous.altitude);
      const t = clamp((targetAlt - previous.altitude) / span, 0, 1);
      return previous.pressure + t * (level.pressure - previous.pressure);
    }
    previous = level;
  }
  return sorted[sorted.length - 1]?.pressure ?? 700;
}

function parcelTemp(surfaceTemp: number, agl: number): number {
  return surfaceTemp - 0.0098 * agl;
}

export function analyzeEmagramHour(levels: SoundingLevel[]): EmagramHourAnalysis {
  const sounding = [...levels]
    .filter((level) => Number.isFinite(level.pressure) && Number.isFinite(level.altitude))
    .sort((a, b) => a.altitude - b.altitude);

  const surface = sounding[0] ?? {
    pressure: 1000,
    altitude: 0,
    temp: 0,
    dewpoint: -5,
    windSpeed: 0,
    windDir: 0,
  };

  const surfaceAlt = surface.altitude;
  const surfaceTemp = surface.temp;
  const surfaceDewpoint = surface.dewpoint;
  const spread = Math.max(0, surfaceTemp - surfaceDewpoint);
  const lclAGL = Math.round(spread * 125);
  const lclPressure = pressureAtAltitude(sounding, lclAGL, surfaceAlt);

  let maxBuoyancy = -99;
  let hasPositiveBuoyancy = false;
  let thermalTopAGL = 0;
  let thermalTopPressure = surface.pressure;

  for (let i = 1; i < sounding.length; i++) {
    const level = sounding[i];
    const agl = Math.max(0, level.altitude - surfaceAlt);
    if (agl > SOARING_ANALYSIS_TOP_AGL) break;
    const diff = parcelTemp(surfaceTemp, agl) - level.temp;
    maxBuoyancy = Math.max(maxBuoyancy, diff);

    if (diff > 0.2) {
      hasPositiveBuoyancy = true;
      thermalTopAGL = agl;
      thermalTopPressure = level.pressure;
      continue;
    }

    if (hasPositiveBuoyancy) {
      const prev = sounding[i - 1];
      const prevAgl = Math.max(0, prev.altitude - surfaceAlt);
      const prevDiff = parcelTemp(surfaceTemp, prevAgl) - prev.temp;
      const denom = prevDiff - diff;
      const frac = Math.abs(denom) > 0.001 ? clamp(prevDiff / denom, 0, 1) : 0;
      thermalTopAGL = Math.round(prevAgl + frac * (agl - prevAgl));
      thermalTopPressure = prev.pressure + frac * (level.pressure - prev.pressure);
      break;
    }
  }

  let inversionBaseAGL: number | null = null;
  let inversionTopAGL: number | null = null;
  for (let i = 1; i < sounding.length; i++) {
    const prev = sounding[i - 1];
    const level = sounding[i];
    const baseAGL = Math.max(0, prev.altitude - surfaceAlt);
    const topAGL = Math.max(0, level.altitude - surfaceAlt);
    if (baseAGL > 4500) break;
    if (level.temp - prev.temp > 0.4) {
      inversionBaseAGL = Math.round(baseAGL);
      inversionTopAGL = Math.round(topAGL);
      break;
    }
  }

  const reachesLcl = hasPositiveBuoyancy && thermalTopAGL >= lclAGL && lclAGL > 150;
  const cloudRisk: EmagramHourAnalysis['cloudRisk'] =
    !reachesLcl ? 'blue' : spread < 4 || lclAGL < 700 ? 'spreadout' : 'cumulus';
  const cumulusBaseAGL = reachesLcl ? lclAGL : 0;
  const usableCeilingAGL = Math.min(thermalTopAGL, SOARING_ANALYSIS_TOP_AGL);

  const layerLevels = sounding.filter((level) => {
    const agl = level.altitude - surfaceAlt;
    return agl >= 0 && agl <= Math.max(600, Math.min(usableCeilingAGL, 2200));
  });
  const windLayerKmh = Math.round(
    layerLevels.reduce((sum, level) => sum + (level.windSpeed || 0), 0) / Math.max(1, layerLevels.length)
  );
  const windSpeeds = layerLevels.map((level) => level.windSpeed || 0);
  const windShearKmh = windSpeeds.length > 0
    ? Math.round(Math.max(...windSpeeds) - Math.min(...windSpeeds))
    : 0;

  const maxVz = hasPositiveBuoyancy
    ? round1(clamp(maxBuoyancy * 0.28 + usableCeilingAGL / 2600, 0, 3.5))
    : 0;

  const limitingFactors: string[] = [];
  if (!hasPositiveBuoyancy) limitingFactors.push('Bulle non portante');
  if (thermalTopAGL < 500) limitingFactors.push('Plafond thermique bas');
  if (inversionBaseAGL !== null && inversionBaseAGL < Math.max(thermalTopAGL + 250, 1200)) limitingFactors.push('Inversion bloquante');
  if (cloudRisk === 'spreadout') limitingFactors.push('Humidite basse couche / etalement');
  if (windLayerKmh > 30) limitingFactors.push('Vent dans la couche convective');
  if (windShearKmh > 18) limitingFactors.push('Cisaillement');

  let status: EmagramHourStatus = 'not-triggered';
  if (!hasPositiveBuoyancy || maxVz < 0.4) status = 'not-triggered';
  else if (inversionBaseAGL !== null && inversionBaseAGL < 900 && thermalTopAGL < 900) status = 'blocked';
  else if (usableCeilingAGL < 700 || maxVz < 1.0) status = 'weak';
  else if (cloudRisk === 'spreadout') status = 'overdeveloping';
  else if (cloudRisk === 'cumulus') status = 'usable-cumulus';
  else status = 'usable-blue';

  const labelMap: Record<EmagramHourStatus, string> = {
    'not-triggered': 'Pas de declenchement',
    blocked: 'Bloque par inversion',
    weak: 'Thermiques faibles',
    'usable-blue': 'Thermiques purs exploitables',
    'usable-cumulus': 'Cumulus exploitable',
    overdeveloping: 'Etalement probable',
  };

  const summary =
    status === 'usable-cumulus'
      ? `Thermiques exploitables jusqu'a ${usableCeilingAGL} m AGL, base vers ${cumulusBaseAGL} m.`
      : status === 'usable-blue'
        ? `Thermiques purs exploitables jusqu'a ${usableCeilingAGL} m AGL.`
        : status === 'blocked'
          ? `Ascendances bloquees bas par une inversion vers ${inversionBaseAGL} m AGL.`
          : status === 'overdeveloping'
            ? `Humidite importante: etalement possible malgre une bulle portante.`
            : status === 'weak'
              ? `Activite faible: plafond ${usableCeilingAGL} m AGL, VZ ${maxVz} m/s.`
              : `La bulle ne devient pas franchement portante.`;

  return {
    status,
    label: labelMap[status],
    summary,
    surfaceTemp: round1(surfaceTemp),
    surfaceDewpoint: round1(surfaceDewpoint),
    lclAGL,
    lclPressure,
    thermalTopAGL: Math.round(thermalTopAGL),
    thermalTopPressure,
    usableCeilingAGL: Math.round(usableCeilingAGL),
    cumulusBaseAGL: Math.round(cumulusBaseAGL),
    maxVz,
    maxBuoyancy: round1(maxBuoyancy),
    inversionBaseAGL,
    inversionTopAGL,
    cloudRisk,
    windLayerKmh,
    windShearKmh,
    limitingFactors,
  };
}
